import { Save, Settings } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { iconButtonStyles } from "../../../Constants/buttonStyles";
import colors from "../../../Constants/colors";
import { inputStyles } from "../../../Constants/inputStyles";
import theme from "../../../Constants/theme";
import {
  UpdateCurrentUserPassword,
  updateUserPersonal,
} from "../../../features/auth/authSlice";
import DeleteOrganizationDialog from "./ManagementComponents/DeleteOrganizationDialog";
import {
  userManagementContainer,
  userManagementPaper,
} from "./UserManagementStyles";
import { GenericSnackbar } from "../../../utils/SnackBars";
import { scrollbarStyles } from "../../../Constants/theme";

const UserProfile = () => {
  const { user } = useSelector((state) => state.auth);
  const organization = useSelector((state) => state.organization);
  const dispatch = useDispatch();
  const [currentUser, setCurrentUser] = useState({
    first_name: user?.user?.first_name,
    last_name: user?.user?.last_name,
  });

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [reEnterNewPassword, setReEnterNewPassword] = useState("");

  const [oldPasswordVisible, setOldPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [reEnterNewPasswordVisible, setReEnterNewPasswordVisible] =
    useState(false);

  // show generic snackbar states
  const [messagesSnackbarOpen, setMessagesSnackbarOpen] = useState(false); // For snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message for snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // Severity of the alert

  const [menuAnchorEl, setMenuAnchorEl] = useState(null); // For menu anchor
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // For delete dialog
  const [orgNameInput, setOrgNameInput] = useState(""); // For organization name inpu
  const organizationName = organization?.organization?.name || "Organization";

  // Open snackbar with a message and severity
  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setMessagesSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setMessagesSnackbarOpen(false);
  };

  const handleCurrentUserChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const isCurrentUserSaveButtonDisabled = () => {
    return (
      currentUser.first_name === user?.user?.first_name &&
      currentUser.last_name === user?.user?.last_name
    );
  };
  const handleCurrentUserSave = async () => {
    const res = await dispatch(
      updateUserPersonal({
        userDetails: currentUser,
        user_id: user?.user?._id["$oid"],
      })
    );
    let cUser = await res.payload.userDetails;
    await setCurrentUser({
      first_name: cUser.first_name,
      last_name: cUser.last_name,
    });
    showSnackbar("User Details updated successfully!", "success");
  };

  const handleResetPassword = async () => {
    const data = {
      oldPassword,
      password: newPassword,
      user_email: user.user.email,
    };

    const res = await dispatch(UpdateCurrentUserPassword(data));

    if (UpdateCurrentUserPassword.fulfilled.match(res)) {
      showSnackbar("Password updated successfully!", "success");
    } else if (UpdateCurrentUserPassword.rejected.match(res)) {
      const errMsg = res.payload || "Something went wrong. Please try again.";

      if (errMsg.includes("Password Invalid")) {
        showSnackbar("Incorrect previous password. Please try again.", "error");
      } else if (errMsg.includes("No user exists")) {
        showSnackbar("User not found. Please check your account.", "error");
      } else if (errMsg.includes("Unable to update password")) {
        showSnackbar("Unable to update password. Try again later.", "error");
      } else {
        showSnackbar(errMsg, "error");
      }
    }

    setOldPassword("");
    setNewPassword("");
    setReEnterNewPassword("");
  };

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };
  const handleOpenDeleteDialog = () => {
    handleCloseMenu();
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setOrgNameInput("");
  };

  return (
    <Box sx={{ ...userManagementContainer, height: "100vh" }}>
      <Paper
        sx={{
          ...userManagementPaper,
          height: "90vh",
          overflowY: "auto",
          ...scrollbarStyles,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            px: 0,
          }}
        >
          {/* Settings Icon */}
          <Tooltip title="Organization Settings">
            <IconButton
              sx={{
                color: colors.extremeWhite,

                "&:hover": {
                  transform: "rotate(90deg)",
                  transition: "0.3s ease",
                },
              }}
              onClick={handleOpenMenu}
            >
              <Settings />
            </IconButton>
          </Tooltip>
        </Box>
        {(user?.user?.privilege === "owner" ||
          user?.user?.privilege === "admin") && (
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: "bottom", // Position the menu below the anchor element
              horizontal: "right", // Align the menu to the right of the anchor element
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: colors.bgDark,
                color: colors.extremeWhite,
                border: `1px solid ${colors.primaryBlue}`,
                borderRadius: 2,
              },
            }}
          >
            <MenuItem
              onClick={handleOpenDeleteDialog}
              sx={{ color: colors.errorRed }}
            >
              Delete Organization
            </MenuItem>
          </Menu>
        )}
        {/* Profile Section */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ color: theme.textColors.dark.primary }}
          >
            Profile
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.textColors.dark.secondary }}
          >
            Update your profile details and click Save.
          </Typography>
        </Box>

        {/* Fields + Save Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            width: "100%",
          }}
        >
          {/* First Name */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.textColors.dark.primary, mb: 1 }}
            >
              First Name
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              name="first_name"
              placeholder="First Name"
              type="text"
              fullWidth
              variant="outlined"
              value={currentUser.first_name}
              onChange={handleCurrentUserChange}
              sx={{
                ...inputStyles.textField,
                backgroundColor: theme.backgroundColors.dark.input,
              }}
            />
          </Box>

          {/* Last Name */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.textColors.dark.primary, mb: 1 }}
            >
              Last Name
            </Typography>
            <TextField
              margin="dense"
              name="last_name"
              placeholder="Last Name"
              type="text"
              fullWidth
              variant="outlined"
              value={currentUser.last_name}
              onChange={handleCurrentUserChange}
              sx={{
                ...inputStyles.textField,
                backgroundColor: theme.backgroundColors.dark.input,
              }}
            />
          </Box>

          {/* Save Button */}

          <Tooltip title="Save Name">
            <span>
              <IconButton
                onClick={handleCurrentUserSave}
                disabled={isCurrentUserSaveButtonDisabled()}
                sx={{ ...iconButtonStyles, mb: 2.5 }}
              >
                <Save />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            width: "100%",
          }}
        >
          {/* Email */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.textColors.dark.primary, mb: 1 }}
            >
              Email
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              name="email"
              placeholder="Email"
              type="text"
              fullWidth
              variant="outlined"
              value={user?.user?.email}
              sx={{
                ...inputStyles.textField,
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.textColors.dark.primary,
                },
              }}
              disabled
            />
          </Box>
          {/* Organization */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.textColors.dark.primary, mb: 1 }}
            >
              Organization
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              name="organization"
              placeholder="Organization"
              type="text"
              fullWidth
              variant="outlined"
              value={organizationName}
              sx={{
                ...inputStyles.textField,
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.textColors.dark.primary,
                },
              }}
              disabled
            />
          </Box>
          {/* Privilige */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.textColors.dark.primary, mb: 1 }}
            >
              Privilege
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              name="privilege"
              placeholder="Privilege"
              type="text"
              fullWidth
              variant="outlined"
              value={user?.user?.privilege}
              sx={{
                ...inputStyles.textField,
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.textColors.dark.primary,
                },
              }}
              disabled
            />
          </Box>
        </Box>
        <Divider
          sx={{
            backgroundColor: theme.border.divider,
            width: "100%",
            mb: 4,
          }}
        />

        {/* Reset Password Section */}
        <Box sx={{ width: "100%" }}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ color: theme.textColors.dark.primary }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.textColors.dark.secondary }}
            >
              Enter your current password, then choose and confirm a new secure
              password. Make sure your new password meets all security
              requirements before saving your changes.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
              width: "100%",
            }}
          >
            {/* Previous Password */}
            <Box sx={{ flex: 1 }}>
              {" "}
              <Typography
                variant="caption"
                sx={{ color: theme.textColors.dark.primary }}
              >
                Previous Password
              </Typography>
              <TextField
                margin="dense"
                name="password"
                placeholder="Previous Password"
                type={oldPasswordVisible ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                sx={{ ...inputStyles.textField }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setOldPasswordVisible(!oldPasswordVisible)}
                    >
                      {oldPasswordVisible ? (
                        <VisibilityOffIcon sx={{ color: "white" }} />
                      ) : (
                        <VisibilityIcon sx={{ color: "white" }} />
                      )}
                    </IconButton>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: theme.textColors.dark.primary }}
              >
                New Password
              </Typography>
              <TextField
                margin="dense"
                name="password"
                placeholder="New Password"
                type={newPasswordVisible ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ ...inputStyles.textField }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setNewPasswordVisible(!newPasswordVisible)}
                    >
                      {newPasswordVisible ? (
                        <VisibilityOffIcon sx={{ color: "white" }} />
                      ) : (
                        <VisibilityIcon sx={{ color: "white" }} />
                      )}
                    </IconButton>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: theme.textColors.dark.primary }}
              >
                Re-enter New Password
              </Typography>
              <TextField
                margin="dense"
                name="password"
                placeholder="Re-enter New Password"
                type={reEnterNewPasswordVisible ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={reEnterNewPassword}
                onChange={(e) => setReEnterNewPassword(e.target.value)}
                sx={{ ...inputStyles.textField }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() =>
                        setReEnterNewPasswordVisible(!reEnterNewPasswordVisible)
                      }
                    >
                      {reEnterNewPasswordVisible ? (
                        <VisibilityOffIcon sx={{ color: "white" }} />
                      ) : (
                        <VisibilityIcon sx={{ color: "white" }} />
                      )}
                    </IconButton>
                  ),
                }}
              />
            </Box>{" "}
            <Tooltip title="Save Password">
              <span>
                <IconButton
                  sx={{ ...iconButtonStyles, mb: 2.5 }}
                  disabled={
                    newPassword !== reEnterNewPassword ||
                    oldPassword === "" ||
                    (newPassword === "" && reEnterNewPassword === "")
                  }
                  onClick={handleResetPassword}
                >
                  <Save />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Delete Organization Dialog */}
        <DeleteOrganizationDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
        />

        {/* Generic Snackbar for alerts */}
        <GenericSnackbar
          open={messagesSnackbarOpen}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          severity={snackbarSeverity}
        />
      </Paper>
    </Box>
  );
};

export default UserProfile;
